import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

const INITIAL_BALANCE = 1000;
const BET_OPTIONS = [0.5, 1, 2, 3, 5, 10, 15, 25, 50, 100, 250, 500];

export interface GameState {
  balance: number;
  bet: number;
  lastWin: number;
  totalWins: number;
  totalSpins: number;
}

interface GameContextType {
  gameState: GameState;
  placeBet: (amount: number) => void;
  addBalance: (amount: number) => void;
  setLastWin: (amount: number) => void;
  recordSpin: (won: boolean) => void;
  betOptions: number[];
}

const defaultState: GameState = {
  balance: INITIAL_BALANCE,
  bet: 10,
  lastWin: 0,
  totalWins: 0,
  totalSpins: 0,
};

const GameContext = createContext<GameContextType | undefined>(undefined);

export function GameProvider({ children }: { children: ReactNode }) {
  const [gameState, setGameState] = useState<GameState>(() => {
    const saved = localStorage.getItem('casinoState');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return { ...defaultState, ...parsed };
      } catch {
        return defaultState;
      }
    }
    return defaultState;
  });

  const saveState = useCallback((state: GameState) => {
    localStorage.setItem('casinoState', JSON.stringify(state));
  }, []);

  const placeBet = useCallback((amount: number) => {
    if (amount <= 0) return;
    setGameState(prev => {
      const next = { ...prev, bet: amount };
      saveState(next);
      return next;
    });
  }, [saveState]);

  const addBalance = useCallback((amount: number) => {
    setGameState(prev => {
      const next = { ...prev, balance: Math.max(0, prev.balance + amount) };
      saveState(next);
      return next;
    });
  }, [saveState]);

  const setLastWin = useCallback((amount: number) => {
    setGameState(prev => {
      const next = {
        ...prev,
        lastWin: amount,
        balance: Math.max(0, prev.balance + amount),
      };
      saveState(next);
      return next;
    });
  }, [saveState]);

  const recordSpin = useCallback((won: boolean) => {
    setGameState(prev => {
      const next = {
        ...prev,
        totalSpins: prev.totalSpins + 1,
        totalWins: prev.totalWins + (won ? 1 : 0),
      };
      saveState(next);
      return next;
    });
  }, [saveState]);

  const value: GameContextType = {
    gameState,
    placeBet,
    addBalance,
    setLastWin,
    recordSpin,
    betOptions: BET_OPTIONS,
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

export function useGame() {
  const ctx = useContext(GameContext);
  if (ctx === undefined) throw new Error('useGame must be used within GameProvider');
  return ctx;
}
